﻿import React from 'react';
import { UnitFormatter } from '../../../formatters';
import { NavDataTooltipData } from '../../../services/map-interaction-service';

interface NavDataTooltipProps {
    item: { layerName: string; properties: any };
}

function NavaidTooltip({ item }: NavDataTooltipProps) {
    const properties = item.properties;

    return (
        <div
            style={{
                width: 150,
                fontSize: 11,
                backgroundColor: 'var(--bg-color)',
                padding: '0.5em'
            }}>
            <div>
                <div style={{ fontSize: '1.1em', fontWeight: 500 }} className="text-center">
                    Navaid: {properties?.id || 'Unknown'}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridColumnGap: '0.25em' }}>
                    <div className="secondary-text-color">Name</div>
                    <div>{properties.name || '???'}</div>

                    <div className="secondary-text-color">Type</div>
                    <div>{formatNavaidType(properties?.type)}</div>

                    <div className="secondary-text-color">MagVar</div>
                    <div>
                        {properties?.magvar != null && <>{properties.magvar}°</>}
                        {properties?.magvar == null && <>Unknown</>}
                    </div>

                    <div className="secondary-text-color">Elevation</div>
                    <div>{properties?.elev ? UnitFormatter.formatDistanceFromFeet(properties.elev) : 'Unknown'}</div>

                    <div className="secondary-text-color">Frequency</div>
                    <div>{properties?.freq || 'Unknown'}</div>
                </div>
            </div>
        </div>
    );
}

function SidTooltip({ item }: NavDataTooltipProps) {
    const properties = item.properties;

    return (
        <div style={{ fontSize: 11, padding: '0.5em', borderBottom: '1px solid var(--border-color)' }}>
            <div style={{ fontSize: '1.1em', fontWeight: 500, color: '#4CAF50' }}>SID: {properties?.name || 'Unknown'}</div>
            <div style={{ marginTop: '0.25em', fontStyle: 'italic', fontSize: 10 }}>Departure procedure</div>
        </div>
    );
}

function StarTooltip({ item }: NavDataTooltipProps) {
    const properties = item.properties;

    return (
        <div style={{ fontSize: 11, padding: '0.5em', borderBottom: '1px solid var(--border-color)' }}>
            <div style={{ fontSize: '1.1em', fontWeight: 500, color: '#FF9800' }}>STAR: {properties?.name || 'Unknown'}</div>
            <div style={{ marginTop: '0.25em', fontStyle: 'italic', fontSize: 10 }}>Arrival procedure</div>
        </div>
    );
}

function FixTooltip({ item }: NavDataTooltipProps) {
    const properties = item.properties;

    return (
        <div style={{ fontSize: 11, padding: '0.5em', borderBottom: '1px solid var(--border-color)' }}>
            <div style={{ fontSize: '1.1em', fontWeight: 500, color: '#9C27B0' }}>
                Fix: {properties?.name || properties?.identifier || 'Unknown'}
            </div>
        </div>
    );
}

const tooltipComponents: Record<string, React.ComponentType<NavDataTooltipProps>> = {
    navaids: NavaidTooltip,
    sids: SidTooltip,
    stars: StarTooltip,
    fixes: FixTooltip
};

export function NavDataTooltipFromItem({ item }: { item: { layerName: string; properties: any } }) {
    const layerType = getLayerType(item.layerName);
    const TooltipComponent = tooltipComponents[layerType];

    if (TooltipComponent) {
        return <TooltipComponent item={item} />;
    }

    // Fallback for unknown types
    return (
        <div style={{ fontSize: 11, padding: '0.5em', borderBottom: '1px solid var(--border-color)' }}>
            <div style={{ fontSize: '1.1em', fontWeight: 500 }}>
                {layerType}: {item.properties?.name || 'Unknown'}
            </div>
        </div>
    );
}

export function NavDataTooltip(props: { info: NavDataTooltipData }) {
    const context = props.info;

    if (!context.items || context.items.length === 0) {
        return <></>;
    }

    return (
        <div
            style={{
                backgroundColor: 'var(--bg-color)',
                maxWidth: 250,
                maxHeight: 300,
                overflowY: 'auto'
            }}>
            <div className="d-flex gap-0 flex-column">
                {context.items.map((item, i) => (
                    <NavDataTooltipFromItem item={item} key={i} />
                ))}
            </div>
        </div>
    );
}

const navaidTypes = {
    ndb: 3,
    vor: 4,
    vorDme: 5,
    vortac: 6,
    tacan: 7,
    dme: 14,
    ndbDme: 11
};

function formatNavaidType(type: any): string {
    if (typeof type === 'number') {
        const typeEntry = Object.entries(navaidTypes).find(([_, value]) => value === type);
        if (typeEntry) {
            const [typeName] = typeEntry;
            switch (typeName) {
                case 'ndb':
                    return 'NDB';
                case 'vor':
                    return 'VOR';
                case 'vorDme':
                    return 'VOR-DME';
                case 'vortac':
                    return 'VORTAC';
                case 'tacan':
                    return 'TACAN';
                case 'dme':
                    return 'DME';
                case 'ndbDme':
                    return 'NDB-DME';
                default:
                    return typeName.toUpperCase();
            }
        }
    }
    return typeof type === 'string' ? type : 'Unknown';
}

function getLayerType(layerName: string): string {
    if (layerName.toLowerCase().includes('navaid')) {
        return 'navaids';
    }
    if (layerName.toLowerCase().includes('sid')) {
        return 'sids';
    }
    if (layerName.toLowerCase().includes('star')) {
        return 'stars';
    }
    if (layerName.toLowerCase().includes('fix')) {
        return 'fixes';
    }
    return layerName;
}
